import type { AuditChange, AuditDomain, AuditMeta } from '@shared/lib/auditLogs'
import { getAuditActor } from './auditActorContext'
import { getLocalDeviceId } from './deviceStorage'
import { getFirestoreInstance, useFirestore } from './firebase'
import { getCurrentPlaceId } from './placeConfig'
import { writeAuditLog } from './firestore/auditLogs'

export interface AppendAuditLogInput {
  domain: AuditDomain
  action: string
  message: string
  entityType?: string
  entityId?: string
  relatedIds?: string[]
  meta?: AuditMeta
  at?: string
  billId?: string
  orderId?: string
  tableId?: string
  amountDelta?: number
  reason?: string
  changes?: AuditChange[]
  customerName?: string
}

const PAY_AUDIT_RETRIES = 3

function buildAuditEntry(input: AppendAuditLogInput) {
  const actor = getAuditActor()
  // Never skip the write for a missing device id — a cleared localStorage must
  // not silently disable auditing while the POS keeps operating.
  const deviceId = getLocalDeviceId() || 'unknown-device'
  return {
    at: input.at ?? new Date().toISOString(),
    domain: input.domain,
    action: input.action,
    message: input.message,
    actorUid: actor.uid,
    actorName: actor.name || 'Staff',
    deviceId,
    entityType: input.entityType,
    entityId: input.entityId,
    relatedIds: input.relatedIds,
    meta: input.meta,
    billId: input.billId,
    orderId: input.orderId,
    tableId: input.tableId,
    amountDelta: input.amountDelta,
    reason: input.reason,
    changes: input.changes,
    customerName: input.customerName,
  }
}

/** Fire-and-forget place audit log. No-op when Firestore is disabled. */
export function appendAuditLog(input: AppendAuditLogInput): void {
  if (!useFirestore()) return
  const placeId = getCurrentPlaceId()
  if (!placeId) return
  const entry = buildAuditEntry(input)

  void (async () => {
    try {
      const db = getFirestoreInstance()
      await writeAuditLog(placeId, db, entry)
    } catch {
      // Swallow — auditing must not break POS flows.
    }
  })()
}

/** Retry pay-money audits; still does not throw to the POS flow. */
export async function appendAuditLogReliable(input: AppendAuditLogInput): Promise<void> {
  if (!useFirestore()) return
  const placeId = getCurrentPlaceId()
  if (!placeId) return
  const entry = buildAuditEntry(input)
  let lastError: unknown
  for (let attempt = 0; attempt < PAY_AUDIT_RETRIES; attempt += 1) {
    try {
      await writeAuditLog(placeId, getFirestoreInstance(), entry)
      return
    } catch (e) {
      lastError = e
      await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)))
    }
  }
  console.error('[audit] pay event failed after retries', input.action, lastError)
}
